import {
  Button,
  IconButton,
  NumberInput,
  Pagination,
  Search,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Add, TrashCan, Undo } from '@carbon/icons-react';
import {
  asProblem,
  EmptyState,
  FilterButton,
  FilterLayout,
  hasPermission,
  problemMessage,
  StatusTag,
  useAdmin,
  useFilters,
  useFormat,
  type RecordValues,
} from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { fold, useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { CellDate } from '../common/CellDate';
import { TableLoading } from '../common/Loading';
import { ErrorNotice, TransientNotice } from '../common/Notices';
import { SaveBar } from '../common/SaveBar';
import { MultiChoice } from '../logs/fields';
import { AddPricesDialog } from './AddPricesDialog';
import {
  addTargets,
  changes,
  fromTable,
  isChanged,
  isNew,
  KINDS,
  problems,
  rowName,
  toRequest,
  unitKey,
  update,
  type Kind,
  type PriceRow,
} from './priceDraft';
import './pricing.scss';

const STATES = ['new', 'changed', 'removed', 'missing'] as const;
const PAGE_SIZES = [25, 50, 100, 200];
const SMALLEST_PAGE = 25;
const PAGE_SIZE = 50;

async function loadTable(id: string) {
  const { data, error } = await hoApi.GET('/api/v1/menu/price-lists/{id}/table', { params: { path: { id } } });
  if (!data) throw error;
  return data;
}

/**
 * A price list's prices as one editable table (FR-MENU-06), in two groups: items (options are items, ADR 0029) and deals. Several can be
 * added at once; prices and dates are edited in place, and everything is saved together.
 */
export function PriceTableTab({ priceList }: { priceList: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, 'ho.menu.price_lists.manage');
  const id = String(priceList.id);
  const queryClient = useQueryClient();
  const key = ['ho', 'price-table', id];
  const table = useQuery({ queryKey: key, queryFn: () => loadTable(id) });
  // The draft starts from the loaded table, and again after each save or reload.
  const [draft, setDraft] = useState<{ source: unknown; rows: PriceRow[] }>();
  const rows = draft && draft.source === table.data ? draft.rows : table.data ? fromTable(table.data) : [];
  const setRows = (next: (rows: PriceRow[]) => PriceRow[]) => setDraft({ source: table.data, rows: next(rows) });
  const [adding, setAdding] = useState<Kind>();
  const [search, setSearch] = useState('');
  const [saved, setSaved] = useState<number>();
  // A price list can hold thousands of rows: each group shows one page of them (its edits are kept across pages).
  const [paging, setPaging] = useState<Record<Kind, { page: number; pageSize: number }>>({
    item: { page: 1, pageSize: PAGE_SIZE },
    deal: { page: 1, pageSize: PAGE_SIZE },
  });
  const filters = useFilters({ kind: '', state: '' });

  const found = problems(rows);
  const pending = changes(rows);

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.PUT('/api/v1/menu/price-lists/{id}/table', {
        params: { path: { id } },
        body: toRequest(rows),
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setSaved(pending);
      setDraft(undefined);
      queryClient.setQueryData(key, next);
    },
  });

  const stateOf = (r: PriceRow) =>
    r.removed
      ? 'removed'
      : isNew(r)
        ? 'new'
        : isChanged(r)
          ? 'changed'
          : found.get(r.key) === 'price'
            ? 'missing'
            : undefined;
  const kinds = filters.applied.kind ? (filters.applied.kind.split(',') as Kind[]) : KINDS;
  const states = filters.applied.state ? filters.applied.state.split(',') : [];
  const shown = rows.filter(
    (r) =>
      kinds.includes(r.kind) &&
      (states.length === 0 ||
        states.includes(stateOf(r) ?? '') ||
        (states.includes('missing') && found.get(r.key) === 'price')) &&
      (!search ||
        fold(`${r.code} ${r.name} ${r.groupName ?? ''} ${r.uomCode ?? ''} ${r.uomName ?? ''}`).includes(fold(search))),
  );
  const problemText = (key: string) =>
    found.get(key) ? t(`priceTable.problems.${found.get(key)}` as I18nKey) : undefined;

  if (table.isLoading) return <TableLoading columns={5} label={t('common.loading')} />;
  if (!table.data)
    return <ErrorNotice title={problemMessage(t, asProblem(table.error))} onRetry={() => void table.refetch()} />;
  const conflict = (save.error as ApiProblem | null)?.code === 'common.version_conflict';
  const dateInvalid = (r: PriceRow, ...kinds: string[]) => kinds.includes(found.get(r.key) ?? '');
  const taxCategories = table.data.taxCategories ?? [];
  const taxName = (categoryId?: string | null) => taxCategories.find((c) => c.id === categoryId)?.name;
  // The default: the item's own VAT; for a deal, its own VAT, else each of its items' (ADR 0033).
  const itemVat = (r: PriceRow) => {
    const own = taxName(r.itemTaxCategoryId);
    if (r.kind === 'deal') return own ? t('priceTable.dealVat', { name: own }) : t('priceTable.dealItemsVat');
    return own ? t('priceTable.itemVat', { name: own }) : t('priceTable.itemVatUnknown');
  };

  return (
    <div className="pos-price-table">
      <p className="pos-price-table__hint">
        {t(table.data.isTaxInclusive ? 'priceTable.taxIncluded' : 'priceTable.taxExcluded', {
          currency: table.data.currencyCode,
        })}
        {Number(table.data.otherEntries) > 0 &&
          ` ${t('priceTable.otherEntries', { count: Number(table.data.otherEntries) })}`}
      </p>
      <p className="pos-price-table__hint">{t('priceTable.unitHint')}</p>
      <p className="pos-price-table__hint">{t('priceTable.vatHint')}</p>
      {saved !== undefined && (
        <TransientNotice
          kind="success"
          title={t('priceTable.saved', { count: saved })}
          onDone={() => setSaved(undefined)}
        />
      )}

      <FilterLayout
        filters={filters}
        fields={
          <>
            <MultiChoice
              id="price-kind"
              label={t('priceTable.group')}
              choices={KINDS.map((k) => ({ id: k, label: t(`priceTable.groups.${k}` as I18nKey) }))}
              value={filters.draft.kind}
              onChange={(v) => filters.set('kind', v)}
            />
            <MultiChoice
              id="price-state"
              label={t('priceTable.state')}
              choices={STATES.map((s) => ({ id: s, label: t(`priceTable.states.${s}` as I18nKey) }))}
              value={filters.draft.state}
              onChange={(v) => filters.set('state', v)}
            />
          </>
        }
      >
        {/* One search and filter for all three tables, so they sit above them, not inside one table's toolbar. */}
        <div className="pos-price-table__tools" role="search">
          <Search
            labelText={t('admin.search')}
            placeholder={t('priceTable.search')}
            closeButtonLabelText={t('carbon.clearSearch')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FilterButton filters={filters} />
        </div>

        {kinds.map((kind) => {
          const matching = shown.filter((r) => r.kind === kind);
          const { pageSize } = paging[kind];
          // A search or filter can shrink the group below the page it was on.
          const page = Math.min(paging[kind].page, Math.max(1, Math.ceil(matching.length / pageSize)));
          const group = matching.slice((page - 1) * pageSize, page * pageSize);
          const count = rows.filter((r) => r.kind === kind && !r.removed).length;
          const addButton = canEdit && (
            <Button kind="tertiary" renderIcon={Add} onClick={() => setAdding(kind)}>
              {t(`priceTable.add.${kind}` as I18nKey)}
            </Button>
          );
          return (
            <section key={kind} className="pos-price-table__group" aria-labelledby={`price-group-${kind}`}>
              <div className="pos-price-table__group-head">
                <h2 id={`price-group-${kind}`} className="pos-price-table__group-title">
                  {t(`priceTable.groups.${kind}` as I18nKey)} <span className="pos-price-table__count">({count})</span>
                </h2>
                {addButton}
              </div>
              <TableContainer>
                {group.length > 0 && (
                  <Table aria-labelledby={`price-group-${kind}`}>
                    <TableHead>
                      <TableRow>
                        <TableHeader>{t('priceTable.columns.code')}</TableHeader>
                        <TableHeader>{t('priceTable.columns.name')}</TableHeader>
                        {kind === 'item' && (
                          <TableHeader className="pos-price-table__unit-col">
                            {t('priceTable.columns.unit')}
                          </TableHeader>
                        )}
                        <TableHeader className="pos-price-table__vat-col">{t('priceTable.columns.vat')}</TableHeader>
                        <TableHeader className="pos-price-table__price-col">
                          {t('priceTable.columns.price', { currency: table.data.currencyCode })}
                        </TableHeader>
                        <TableHeader className="pos-price-table__date-col">{t('priceTable.columns.from')}</TableHeader>
                        <TableHeader className="pos-price-table__date-col">{t('priceTable.columns.to')}</TableHeader>
                        {canEdit && <TableHeader aria-label={t('admin.actions')} />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.map((r) => {
                        const state = stateOf(r);
                        return (
                          <TableRow key={r.key} className={state ? `pos-price-table__row--${state}` : undefined}>
                            <TableCell className="pos-price-table__code">{r.code}</TableCell>
                            <TableCell>
                              <span className="pos-price-table__name">
                                {r.groupName ? `${r.groupName} › ${r.name}` : r.name}
                              </span>
                              {state && state !== 'missing' && (
                                <StatusTag
                                  status={state === 'removed' ? 'failed' : state === 'new' ? 'active' : 'open'}
                                >
                                  {t(`priceTable.states.${state}` as I18nKey)}
                                </StatusTag>
                              )}
                              {!r.isActive && <StatusTag status="inactive">{t('priceTable.inactive')}</StatusTag>}
                            </TableCell>
                            {kind === 'item' && (
                              <TableCell className="pos-price-table__unit-col">
                                {/* Another of the item's units; a unit it isn't sold in can't be picked. */}
                                {canEdit && !r.removed && r.units.length > 1 ? (
                                  <Select
                                    id={`unit-${r.key}`}
                                    labelText={t('priceTable.unitOf', { name: r.name })}
                                    hideLabel
                                    value={r.uomId ?? ''}
                                    invalid={found.get(r.key) === 'duplicate'}
                                    onChange={(e) => setRows((rs) => update(rs, r.key, { uomId: e.target.value }))}
                                  >
                                    {r.units.map((u) => (
                                      <SelectItem key={u.uomId} value={u.uomId} text={u.code} />
                                    ))}
                                  </Select>
                                ) : (
                                  <span className="pos-price-table__code">{r.uomCode}</span>
                                )}
                              </TableCell>
                            )}
                            <TableCell className="pos-price-table__vat-col">
                              {/* One VAT category for the item or deal on this list: changing it here changes all its rows. */}
                              {canEdit && !r.removed ? (
                                <Select
                                  id={`vat-${r.key}`}
                                  labelText={t('priceTable.vatOf', { name: rowName(r) })}
                                  hideLabel
                                  value={r.taxCategoryId ?? ''}
                                  onChange={(e) =>
                                    setRows((rs) => update(rs, r.key, { taxCategoryId: e.target.value || null }))
                                  }
                                >
                                  <SelectItem value="" text={itemVat(r)} />
                                  {taxCategories.map((c) => (
                                    <SelectItem key={c.id} value={c.id} text={c.name} />
                                  ))}
                                </Select>
                              ) : (
                                (taxName(r.taxCategoryId) ?? itemVat(r))
                              )}
                            </TableCell>
                            <TableCell className="pos-price-table__price-col">
                              {canEdit && !r.removed ? (
                                <NumberInput
                                  id={`price-${r.key}`}
                                  label={t('priceTable.priceOf', { name: rowName(r) })}
                                  hideLabel
                                  hideSteppers
                                  allowEmpty
                                  min={0}
                                  value={r.price}
                                  invalid={found.get(r.key) === 'price'}
                                  invalidText={problemText(r.key)}
                                  onChange={(_, { value }) =>
                                    setRows((rs) =>
                                      update(rs, r.key, {
                                        price: value === '' || value === undefined ? '' : String(value),
                                      }),
                                    )
                                  }
                                />
                              ) : (
                                <span className="pos-price-table__number">
                                  {r.price === '' ? '—' : format.number(r.price)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="pos-price-table__date-col">
                              {canEdit && !r.removed ? (
                                <CellDate
                                  id={`from-${r.key}`}
                                  labelText={t('priceTable.fromOf', { name: rowName(r) })}
                                  value={r.from}
                                  invalid={dateInvalid(r, 'from', 'duplicate')}
                                  invalidText={problemText(r.key)}
                                  onChange={(value) => setRows((rs) => update(rs, r.key, { from: value }))}
                                />
                              ) : (
                                r.from
                              )}
                            </TableCell>
                            <TableCell className="pos-price-table__date-col">
                              {canEdit && !r.removed ? (
                                <CellDate
                                  id={`to-${r.key}`}
                                  labelText={t('priceTable.toOf', { name: rowName(r) })}
                                  value={r.to}
                                  invalid={dateInvalid(r, 'dates')}
                                  invalidText={problemText(r.key)}
                                  onChange={(value) => setRows((rs) => update(rs, r.key, { to: value }))}
                                />
                              ) : (
                                r.to || '—'
                              )}
                            </TableCell>
                            {canEdit && (
                              <TableCell className="pos-admin-page__actions">
                                {r.removed ? (
                                  <IconButton
                                    kind="ghost"
                                    label={t('priceTable.undoRemove', { name: rowName(r) })}
                                    onClick={() => setRows((rs) => update(rs, r.key, { removed: false }))}
                                  >
                                    <Undo />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    kind="ghost"
                                    label={t('priceTable.remove', { name: rowName(r) })}
                                    onClick={() => setRows((rs) => update(rs, r.key, { removed: true }))}
                                  >
                                    <TrashCan />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
                {matching.length > SMALLEST_PAGE && (
                  <Pagination
                    itemsPerPageText={t('carbon.itemsPerPage')}
                    itemRangeText={(min, max, total) => t('carbon.itemRange', { min, max, total })}
                    pageRangeText={(_current, total) => t('carbon.pageRange', { total })}
                    backwardText={t('carbon.previousPage')}
                    forwardText={t('carbon.nextPage')}
                    pageNumberText={t('carbon.pageNumber')}
                    page={page}
                    pageSize={pageSize}
                    pageSizes={PAGE_SIZES}
                    totalItems={matching.length}
                    onChange={(next) =>
                      setPaging((all) => ({ ...all, [kind]: { page: next.page, pageSize: next.pageSize } }))
                    }
                  />
                )}
                {group.length === 0 &&
                  (count > 0 ? (
                    <EmptyState
                      headingLevel={3}
                      heading={t('admin.noResultsTitle')}
                      description={t('admin.noResultsHint')}
                    />
                  ) : (
                    <EmptyState
                      headingLevel={3}
                      heading={t(`priceTable.emptyTitle.${kind}` as I18nKey)}
                      description={t(`priceTable.empty.${kind}` as I18nKey)}
                    />
                  ))}
              </TableContainer>
            </section>
          );
        })}
      </FilterLayout>

      {canEdit && (pending > 0 || save.error) && (
        <SaveBar
          summary={t('priceTable.unsaved', { count: pending })}
          problems={[
            ...(found.size > 0 ? [t('priceTable.fix', { count: found.size })] : []),
            ...(save.error ? [conflict ? t('priceTable.conflict') : problemMessage(t, asProblem(save.error))] : []),
          ]}
          discardText={conflict ? t('priceTable.reload') : t('priceTable.discard')}
          saveText={t('priceTable.save', { count: pending })}
          saveDisabled={found.size > 0 || pending === 0}
          saving={save.isPending}
          onDiscard={() => {
            save.reset();
            setDraft(undefined);
            if (conflict) void table.refetch();
          }}
          onSave={() => save.mutate()}
        />
      )}

      {adding && (
        <AddPricesDialog
          priceListId={id}
          kind={adding}
          inTable={new Set(rows.filter((r) => !r.removed).map((r) => unitKey(r.targetId, r.uomId)))}
          onAdd={(targets, defaults) => setRows((rs) => addTargets(rs, adding, targets, defaults))}
          onClose={() => setAdding(undefined)}
        />
      )}
    </div>
  );
}
